import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import UserAvatar from './UserAvatar';
import logo from '../assets/logo.png';
import './Navbar.css';

const Navbar = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const { language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const navbarRef = useRef(null);

  const portalPath = useMemo(() => {
    if (!isAuthenticated) return '/login';
    return user?.role === 'student' ? '/student' : '/admin';
  }, [isAuthenticated, user?.role]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setActiveMenu(null);
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    logout();
    navigate('/');
  };

  const toggleMenu = (menuName) => {
    setIsUserMenuOpen(false);
    setActiveMenu((prev) => (prev === menuName ? null : menuName));
  };

  return (
    <nav className="navbar" ref={navbarRef}>
      <div className="navbar-container">
        <Link to="/" className="navbar-logo" onClick={() => setActiveMenu(null)}>
          <img src={logo} alt="NLBA" className="navbar-logo-image" />
          <span className="navbar-logo-text">NLBA</span>
        </Link>

        <div className="navbar-links">
          <Link to="/" className="navbar-link" onClick={() => setActiveMenu(null)}>{t('nav.home')}</Link>

          <div className="navbar-dropdown">
            <button
              type="button"
              className="navbar-dropdown-trigger"
              onClick={() => toggleMenu('about')}
              aria-expanded={activeMenu === 'about'}
              aria-haspopup="true"
            >
              Over NLBA
              <span className="navbar-chevron">▾</span>
            </button>
            {activeMenu === 'about' && (
              <div className="navbar-dropdown-menu">
                <Link to="/over-nlba/waarom-kies-je-voor-ons" className="navbar-dropdown-item" onClick={() => setActiveMenu(null)}>
                  Waarom kies je voor ons
                </Link>
                <Link to="/over-nlba/accreditatie" className="navbar-dropdown-item" onClick={() => setActiveMenu(null)}>
                  Accreditatie
                </Link>
                <Link to="/over-nlba/reviews" className="navbar-dropdown-item" onClick={() => setActiveMenu(null)}>
                  Recensies
                </Link>
                <Link to="/over-nlba/ons-team" className="navbar-dropdown-item" onClick={() => setActiveMenu(null)}>
                  Ons Team
                </Link>
                <Link to="/over-nlba/organogram" className="navbar-dropdown-item" onClick={() => setActiveMenu(null)}>
                  Organogram
                </Link>
              </div>
            )}
          </div>

          <div className="navbar-dropdown">
            <button
              type="button"
              className="navbar-dropdown-trigger"
              onClick={() => toggleMenu('education')}
              aria-expanded={activeMenu === 'education'}
              aria-haspopup="true"
            >
              Opleidingen
              <span className="navbar-chevron">▾</span>
            </button>
            {activeMenu === 'education' && (
              <div className="navbar-dropdown-menu">
                <Link to="/brochures/hbo" className="navbar-dropdown-item" onClick={() => setActiveMenu(null)}>
                  HBO Bedrijfskunde (BBA)
                </Link>
                <Link to="/brochures/master" className="navbar-dropdown-item" onClick={() => setActiveMenu(null)}>
                  Masteropleidingen
                </Link>
                <Link to="/brochures/masterclasses" className="navbar-dropdown-item" onClick={() => setActiveMenu(null)}>
                  Masterclasses
                </Link>
                <Link to="/studiefinanciering" className="navbar-dropdown-item" onClick={() => setActiveMenu(null)}>
                  Studiefinanciering
                </Link>
              </div>
            )}
          </div>

          <Link to="/news" className="navbar-link" onClick={() => setActiveMenu(null)}>{t('nav.news')}</Link>
          <Link to="/contact" className="navbar-link" onClick={() => setActiveMenu(null)}>Contact</Link>
        </div>

        <div className="navbar-right">
          <button onClick={toggleLanguage} className="navbar-language-button">
            {language === 'nl' ? 'EN' : 'NL'}
          </button>

          {isAuthenticated ? (
            <div className="navbar-account-wrap">
              <Link to={portalPath} className="navbar-portal-button">
                Portaal
              </Link>

              <button
                type="button"
                className="navbar-avatar-button"
                onClick={() => {
                  setActiveMenu(null);
                  setIsUserMenuOpen((prev) => !prev);
                }}
                aria-expanded={isUserMenuOpen}
                aria-haspopup="true"
                title={user?.fullName || 'Gebruiker'}
              >
                <UserAvatar user={user} size="sm" />
              </button>

              {isUserMenuOpen && (
                <div className="navbar-user-menu">
                  <div className="navbar-user-meta">
                    <strong>{user?.fullName || 'Gebruiker'}</strong>
                    <small>{t(`roles.${user?.role}`)}</small>
                  </div>
                  <button onClick={handleLogout} className="navbar-logout-button">
                    {t('nav.logout')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className="navbar-login-button">
              Inloggen
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
